import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, Grid, Hidden, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { routes } from 'utils/routing';
import { BoxContainer, Content, SideBar } from 'components/common/BoxContainer';
import BottomNavigation from 'components/common/BottomNavigation';
import Agreement from 'components/common/Agreement';
import EventInfo from 'components/event/EventInfo';
import EventOrganization from 'components/event/EventOrganization';
import CheckoutCollapsibleStepper from 'components/creatingOrder/CheckoutCollapsibleStepper';
import OrderItemsContainer from 'containers/OrderItemsContainer';
import OrderSummaryContainer from 'containers/OrderSummaryContainer';
import Button from 'components/common/Button/Button';
import { useRouter } from 'next/router';
import BackButton from '../common/BackButton';
import { trackPage } from './ScrenTracker';

export interface EventTicketsScreenProps {
  frame?: boolean;
  event: any;
  hasTicketsCount: boolean;
  totalTicketsCount: number;
}

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isStagingEnv = () => BUILD_ENV === 'staging';
const isProductionEnv = () => BUILD_ENV === 'production';

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    padding: theme.spacing(5, 4),
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      marginTop: 0,
      padding: 0,
    },
  },
  OrderContentContainer: {
    [theme.breakpoints.down('md')]: {
      padding: '20px 20px 12px 16px !important',
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  actionButton: {
    minWidth: 152,
  },
  mobileTop: { position: 'sticky', top: 64, width: '100%', zIndex: 199 },
  mobileBottom: { position: 'sticky', bottom: 0, width: '100%', zIndex: 1 },
}));

const EventTicketsScreen: React.FC<React.PropsWithChildren<EventTicketsScreenProps>> = (props) => {
  const { frame, event, hasTicketsCount, totalTicketsCount } = props;
  const { classes } = useStyles();
  const router = useRouter();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));
  const heightOffset = isMobile ? 224 : 116;
  const [collapsed, setCollapsed] = useState(false);
  const [t] = useTranslation('public');
  const [windowHeight, setWindowHeight] = useState('100vh');

  const top = <CheckoutCollapsibleStepper activeStep={0} collapsed={collapsed} />;

  const admin = router.query.admin === String(true);
  const isNotOnSale = useMemo(() => {
    if (admin && event?.status_display === 'past') return false;
    return event?.tickets_sale_status !== 'on_sale';
  }, [admin, event?.status_display, event?.tickets_sale_status]);

  useEffect(() => {
    setWindowHeight(`${window.innerHeight}px`);
    if (event?.id) trackPage('Event Tickets Page', { event_id: event.id });
  }, [event?.id]);

  const goBack = useCallback(() => {
    const route = routes.event(event.id);
    router.push(route.href);
  }, [router, event]);

  const goContinue = useCallback(() => {
    if (isMobile) {
      router.push(routes.eventTicketsSummary(event.id).href);
    } else {
      router.push(routes.eventRegistration(event.id).href);
    }
  }, [event.id, isMobile, router]);

  const bottom = (
    <Collapse in>
      <BottomNavigation isCheckoutStyle description={<Agreement />}>
        <div className={classes.buttonsContainer}>
          <BackButton
            backButtonStyle="blue"
            buttonLabel={t<string>('event.eventTickets.backButton')}
            onClick={goBack}
          />
          <Button
            disabled={!hasTicketsCount}
            rounded
            className={classes.actionButton}
            id="continue-button"
            variant="contained"
            color="primary"
            onClick={goContinue}
          >
            {t<string>('common:button.continue')}
          </Button>
        </div>
      </BottomNavigation>
    </Collapse>
  );

  const showSummary = useMemo(() => {
    if ((event.id === 6047 && isProductionEnv()) || (event.id === 1953 && isTestEnv())) return false;
    return hasTicketsCount;
  }, [event.id, hasTicketsCount]);

  const canPurchaseMoreTickets = () => {
    const hasMoreTickets = event.settings.free_places > totalTicketsCount;
    if (event.settings.is_total_capacity_enabled && event.settings.capacity) {
      return hasMoreTickets;
    }
    return true;
  };

  const handleOnChangeYOffset = (value: any) => {
    setCollapsed(Boolean(value));
  };

  const firstAddons = useMemo(() => {
    if (isProductionEnv() && event.id === 10939) return true;
    if (isStagingEnv() && event.id === 437) return true;
    if (isTestEnv() && event.id === 1904) return true;
    return false;
  }, [event.id]);

  return (
    <BoxContainer overflow frame={frame}>
      {isMobile && <div className={classes.mobileTop}>{top}</div>}
      <Content
        style={{ minHeight: `calc(${windowHeight} - ${heightOffset}px)` }}
        top={!isMobile && top}
        bottom={!isMobile ? bottom : <span />}
        onChangeYOffset={handleOnChangeYOffset}
      >
        <Grid className={classes.contentContainer} container direction="column" wrap="nowrap" spacing={5}>
          {isMobile && <EventInfo event={event} disabled={frame} secondary dateTimeTag />}
          {firstAddons ? (
            <>
              <Grid item className={classes.OrderContentContainer}>
                <OrderItemsContainer itemType="addon" disabled={isNotOnSale} />
              </Grid>
              <Grid item className={classes.OrderContentContainer}>
                <OrderItemsContainer
                  itemType="ticket"
                  disabled={isNotOnSale}
                  maxAmountReached={!canPurchaseMoreTickets()}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item className={classes.OrderContentContainer}>
                <OrderItemsContainer
                  itemType="ticket"
                  disabled={isNotOnSale}
                  maxAmountReached={!canPurchaseMoreTickets()}
                />
              </Grid>
              <Grid item className={classes.OrderContentContainer}>
                <OrderItemsContainer itemType="addon" disabled={isNotOnSale} />
              </Grid>
            </>
          )}
        </Grid>
      </Content>
      <Hidden mdUp>
        <div className={classes.mobileBottom}>{bottom}</div>
      </Hidden>
      <Hidden mdDown>
        <SideBar>
          <EventInfo event={event} disabled={frame} secondary dateTimeTag multipleTimeTag>
            <Grid container direction="column" wrap="nowrap" justifyContent="flex-end" spacing={4}>
              {showSummary && (
                <Grid item xs>
                  <OrderSummaryContainer />
                </Grid>
              )}
              <Grid item>
                <EventOrganization disabled={frame} organization={event.organization} />
              </Grid>
            </Grid>
          </EventInfo>
        </SideBar>
      </Hidden>
    </BoxContainer>
  );
};

export default EventTicketsScreen;
